/**
 * Raw mutation service fns for asset-level approval chain assignment.
 *
 * The matching read service + queryOptions helper (getAssetApprovalChain,
 * assetApprovalChainQuery) live canonically in `entities/asset-approval-chain`. The
 * mutations belong to this feature per AD-23.
 *
 * Each call suppresses the global error toast (SUPPRESS_ERROR_NOTIFICATION_HEADER)
 * so callers own the error UX via `parseProblemDetail` + `notificationService`,
 * matching the approval-chain definitions feature (`features/manage-approval-chains`).
 */
import {
  AssetWorkflowAssignmentService,
  BulkWorkflowAssignmentService,
  type AssetWorkflowResponseDto,
  type BulkAssignWorkflowResponseDto,
  type BulkUnassignWorkflowResponseDto,
} from '@inference.engine/workspace-api-client/web';

import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';

export const assignAssetApprovalChain = async (
  projectId: string,
  assetId: string,
  workflowDefinitionId: string,
): Promise<AssetWorkflowResponseDto> => {
  const response = await AssetWorkflowAssignmentService.assign({
    client: workspaceHttp,
    path: { platformProjectId: projectId, assetId },
    body: { workflowDefinitionId },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to assign approval chain to asset');
  }

  return response.data;
};

export const removeAssetApprovalChain = async (
  projectId: string,
  assetId: string,
): Promise<void> => {
  await AssetWorkflowAssignmentService.remove({
    client: workspaceHttp,
    path: { platformProjectId: projectId, assetId },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });
};

export const bulkAssignApprovalChain = async (
  projectId: string,
  approvalChainId: string,
  assetIds: string[],
  overrideExisting: boolean,
): Promise<BulkAssignWorkflowResponseDto> => {
  const response = await BulkWorkflowAssignmentService.bulkAssign({
    client: workspaceHttp,
    path: { platformProjectId: projectId, workflowId: approvalChainId },
    body: { assetIds, overrideExisting },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to bulk-assign approval chain');
  }

  return response.data;
};

export const bulkUnassignApprovalChain = async (
  projectId: string,
  approvalChainId: string,
  assetIds: string[],
): Promise<BulkUnassignWorkflowResponseDto> => {
  const response = await BulkWorkflowAssignmentService.bulkUnassign({
    client: workspaceHttp,
    path: { platformProjectId: projectId, workflowId: approvalChainId },
    body: { assetIds },
    // eslint-disable-next-line i18next/no-literal-string -- request header value, never rendered
    headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' },
  });

  if (!response.data) {
    throw new Error('Failed to bulk-unassign approval chain');
  }

  return response.data;
};
